import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Rating,
    Button,
    Skeleton,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Card,
    CardMedia,
    Divider,
    Stack,
    IconButton
} from '@mui/material';
import { ShoppingCart, CheckCircle, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { addToCart } from '../redux/shopSlice';
import './css/product-display.css';
import { toast } from "react-toastify";

const ProductDisplay = ({ product, loading }) => {
    const [selectedVariations, setSelectedVariations] = useState({});
    const [btnState, setBtnState] = useState('idle');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [calculatedPrice, setCalculatedPrice] = useState(0);
    const dispatch = useDispatch();
    const S3Url = process.env.REACT_APP_S3_PATH;

    const { _id, images = [], name, old_price, new_price, variations = [] } = product || {};

    const defaultRating = 4;
    const reviewCount = 122;
    const discountPercent = old_price ? Math.round(((old_price - new_price) / old_price) * 100) : 0;

    // Calculate final price based on selected variations
    useEffect(() => {
        let totalIncrease = 0;
        variations.forEach(variation => {
            const selectedValue = selectedVariations[variation.name];
            if (selectedValue) {
                const selectedOption = variation.options.find(opt => opt.value === selectedValue);
                if (selectedOption) {
                    totalIncrease += selectedOption.priceIncrease || 0;
                }
            }
        });
        setCalculatedPrice(new_price + totalIncrease);
    }, [selectedVariations, new_price, variations]);

    const handleVariationChange = (variationName, value) => {
        setSelectedVariations(prev => ({
            ...prev,
            [variationName]: value
        }));
    };

    const handleAdd = async () => {
        // Check if all variations are selected
        const missingVariations = variations.filter(
            variation => !selectedVariations[variation.name]
        );

        if (missingVariations.length > 0) {
            return toast.error(`Please select ${missingVariations[0].name}`);
        }

        try {
            setBtnState('loading');
            const res = await dispatch(addToCart({
                productId: _id,
                unitPrice: calculatedPrice,
                variations: selectedVariations
            })).unwrap();

            if (res.success) {
                setBtnState("added");
                setTimeout(() => setBtnState("idle"), 2000);
            } else {
                setBtnState("idle");
            }
        } catch (error) {
            toast.error("Something went wrong!");
            setBtnState("idle");
        }
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleTouchStart = (e) => {
        const touchStart = e.touches[0].clientX;
        e.currentTarget.dataset.touchStart = touchStart;
    };

    const handleTouchEnd = (e) => {
        const touchStart = parseFloat(e.currentTarget.dataset.touchStart);
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStart - touchEnd;

        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                handleNextImage();
            } else {
                handlePrevImage();
            }
        }
    };

    if (loading) {
        return (
            <Box className="product-display product-skeleton">
                <Box className="product-display-left">
                    <Skeleton variant="rectangular" className="product-skeleton-img" />
                </Box>
                <Box className="product-display-right">
                    <Skeleton variant="text" className="skeleton-line lg" />
                    <Skeleton variant="text" className="skeleton-line md" />
                    <Skeleton variant="rectangular" className="skeleton-box" />
                </Box>
            </Box>
        )
    }

    const currentImage = images[currentImageIndex];

    return (
        <Box className="product-display">
            {/* Left - Image Carousel */}
            <Box className="product-display-left">
                <Card className="product-card">
                    {discountPercent > 0 && (
                        <Box className="discount-chip">
                            {discountPercent}% OFF
                        </Box>
                    )}

                    <Box
                        className="image-carousel"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <CardMedia
                            component="img"
                            image={S3Url + currentImage?.name}
                            alt={name}
                            className="product-img"
                        />

                        {images.length > 1 && (
                            <>
                                <IconButton
                                    className="carousel-btn carousel-btn-left"
                                    onClick={handlePrevImage}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                <IconButton
                                    className="carousel-btn carousel-btn-right"
                                    onClick={handleNextImage}
                                >
                                    <ChevronRight />
                                </IconButton>

                                {/* Image Indicators */}
                                <Box className="image-indicators">
                                    {images.map((_, index) => (
                                        <Box
                                            key={index}
                                            className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Thumbnail Strip */}
                    {images.length > 1 && (
                        <Box className="thumbnail-strip">
                            {images.map((img, index) => (
                                <Box
                                    key={index}
                                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                >
                                    <img src={S3Url + img.name} alt={`${name} ${index + 1}`} />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Card>
            </Box>

            {/* Right - Details */}
            <Box className="product-display-right">
                <Typography variant="h4" className="product-title">{name}</Typography>

                <Stack direction="row" alignItems="center" spacing={1} className="rating-row">
                    <Rating value={defaultRating} readOnly precision={0.5} size="medium" />
                    <Typography variant="body2" className="review-count">({reviewCount} reviews)</Typography>
                </Stack>

                <Divider className="divider" />

                <Box className="price-box">
                    <Stack direction="row" alignItems="baseline" spacing={2}>
                        <Typography variant="h3" className="new-price">${calculatedPrice.toFixed(2)}</Typography>
                        {old_price && <Typography variant="h5" className="old-price">${old_price}</Typography>}
                    </Stack>
                    <Typography variant="body2" className="discount-text">
                        You save ${(old_price - calculatedPrice).toFixed(2)} ({Math.round(((old_price - calculatedPrice) / old_price) * 100)}%)
                    </Typography>
                </Box>

                {/* Variations Section */}
                {variations.length > 0 && (
                    <Box className='variation-section'>
                        {variations.map(variation => (
                            <FormControl key={variation.id} component="fieldset" className="variation-group">
                                <FormLabel>Select {variation.name}</FormLabel>
                                <RadioGroup
                                    row
                                    value={selectedVariations[variation.name] || ''}
                                    onChange={(e) => handleVariationChange(variation.name, e.target.value)}
                                >
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                        {variation.options.map(option => {
                                            const isSelected = selectedVariations[variation.name] === option.value;
                                            return (
                                                <FormControlLabel
                                                    key={option.id}
                                                    value={option.value}
                                                    control={<Radio className="hidden-radio" />}
                                                    label={
                                                        <Box className={`variation-option ${isSelected ? 'selected' : ''}`}>
                                                            <span className="variation-value">{option.value}</span>
                                                        </Box>
                                                    }
                                                    className="variation-label"
                                                />
                                            );
                                        })}
                                    </Stack>
                                </RadioGroup>
                            </FormControl>
                        ))}
                    </Box>
                )}

                <Box className="cart-actions">
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={btnState === "added" ? <CheckCircle /> : <ShoppingCart />}
                        onClick={handleAdd}
                        className={`add-cart-btn ${btnState === "added" ? "added" : ""}`}
                        disabled={btnState === "loading"}
                    >
                        {btnState === "loading" ? "Adding..." : btnState === "added" ? "Added!" : "Add to Cart"}
                    </Button>
                </Box>

                <Divider className="divider" />
            </Box>
        </Box>
    );
};

export default ProductDisplay;